import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { PanelState, PanelKey, CommunicationPanelStates, PanelPosition, PoppedOutState } from '@/types/communication-panels';

const POPPED_KEY = 'comm_panel_popped';
const POS_KEY = 'comm_panel_positions';

const DEFAULT_STATES: CommunicationPanelStates = {
  omstilling: 'closed',
  ai_assistent: 'closed',
  intern_chat: 'closed',
};

function getDefaultPositions(): Record<PanelKey, PanelPosition> {
  const w = typeof window !== 'undefined' ? window.innerWidth : 1400;
  return {
    ai_assistent: { x: w - 420, y: 80, width: 380, height: 520 },
    omstilling: { x: 20, y: 80, width: 380, height: 520 },
    intern_chat: { x: Math.round(w / 2 - 190), y: 80, width: 380, height: 520 },
  };
}

function loadPopped(): PoppedOutState {
  try {
    const raw = sessionStorage.getItem(POPPED_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { omstilling: false, ai_assistent: false, intern_chat: false };
}

function loadPositions(): Record<PanelKey, PanelPosition> {
  try {
    const raw = sessionStorage.getItem(POS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return getDefaultPositions();
}

export function usePanelPreferences() {
  const { user } = useAuth();
  const [states, setStates] = useState<CommunicationPanelStates>(DEFAULT_STATES);
  const [poppedOut, setPoppedOut] = useState<PoppedOutState>(loadPopped);
  const [positions, setPositions] = useState<Record<PanelKey, PanelPosition>>(loadPositions);
  const [loaded, setLoaded] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestRef = useRef(states);

  // Load from DB
  useEffect(() => {
    if (!user?.id || !user?.company_id) return;
    (async () => {
      const { data } = await (supabase as any)
        .from('user_preferences')
        .select('preferences')
        .eq('user_id', user.id)
        .eq('company_id', user.company_id)
        .maybeSingle();
      const saved = data?.preferences?.communication_panels;
      if (saved) {
        setStates(prev => {
          const next = { ...prev };
          for (const key of ['omstilling', 'ai_assistent', 'intern_chat'] as const) {
            if (saved[key]) next[key] = saved[key] as PanelState;
          }
          latestRef.current = next;
          return next;
        });
      }
      setLoaded(true);
    })();
  }, [user?.id, user?.company_id]);

  // Persist positions to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(POS_KEY, JSON.stringify(positions));
  }, [positions]);

  // Persist popped state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(POPPED_KEY, JSON.stringify(poppedOut));
  }, [poppedOut]);

  // Debounced save to DB
  const persistStates = useCallback((newStates: CommunicationPanelStates) => {
    if (!user?.id || !user?.company_id) return;
    latestRef.current = newStates;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const { data: existing } = await (supabase as any)
        .from('user_preferences')
        .select('preferences')
        .eq('user_id', user.id)
        .eq('company_id', user.company_id)
        .maybeSingle();
      const merged = {
        ...(existing?.preferences || {}),
        communication_panels: latestRef.current,
      };
      await (supabase as any)
        .from('user_preferences')
        .upsert(
          { user_id: user.id, company_id: user.company_id, preferences: merged, updated_at: new Date().toISOString() },
          { onConflict: 'user_id,company_id' },
        );
    }, 500);
  }, [user?.id, user?.company_id]);

  const setPanelState = useCallback((panel: PanelKey, state: PanelState) => {
    setStates(prev => {
      const next = { ...prev, [panel]: state };
      persistStates(next);
      latestRef.current = next;
      return next;
    });
  }, [persistStates]);

  const popOut = useCallback((panel: PanelKey) => {
    setPoppedOut(prev => ({ ...prev, [panel]: true }));
  }, []);

  const dock = useCallback((panel: PanelKey) => {
    setPoppedOut(prev => ({ ...prev, [panel]: false }));
  }, []);

  const updatePosition = useCallback((panel: PanelKey, pos: Partial<PanelPosition>) => {
    setPositions(prev => ({ ...prev, [panel]: { ...prev[panel], ...pos } }));
  }, []);

  return { states, poppedOut, positions, setPanelState, popOut, dock, updatePosition, loaded };
}
